import { useState } from "react";
import { ArrowLeft, Loader2, CheckCircle, Building2, Mail, User, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TrialRegisterPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    cnpj: "",
    position: "",
    employees: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all required fields
    if (!formData.name || !formData.email || !formData.company || !formData.cnpj || !formData.position || !formData.employees) {
      toast.error("Todos os campos são obrigatórios");
      return;
    }
    
    setIsLoading(true);

    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        cnpj: formData.cnpj,
        position: formData.position,
        employees: formData.employees,
        source: "trial_register",
      });
      
      setIsSuccess(true);
      toast.success("Cadastro realizado com sucesso!");
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao realizar cadastro.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-[#0F1115] flex items-center justify-center px-6" data-testid="trial-register-success">
        <div className="max-w-md w-full">
          <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-8 text-center">
            <div className="w-16 h-16 bg-[#10B981]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-[#10B981]" />
            </div>
            
            <h1 className="font-heading text-2xl font-bold text-[#E5E7EB] mb-4">
              Cadastro Realizado!
            </h1>
            
            <p className="text-[#9CA3AF] mb-6">
              Após a validação do seu cadastro, você receberá as instruções para acesso ao software.
            </p>

            <div className="bg-[#0F1115] border border-[#2A2F38] rounded-sm p-4 mb-6">
              <p className="text-sm text-[#6B7280] mb-2">Próximos passos:</p>
              <ul className="text-sm text-[#9CA3AF] text-left space-y-2">
                <li>1. Aguarde a validação do cadastro</li>
                <li>2. Receba as instruções de acesso</li>
                <li>3. Faça o download e comece a usar</li>
              </ul>
            </div>

            <Button
              onClick={() => window.location.href = "/"}
              className="bg-[#F59E0B] text-black font-bold uppercase tracking-wide hover:bg-[#D97706] rounded-sm px-6 py-3"
            >
              Voltar para Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1115]" data-testid="trial-register-page">
      {/* Header */}
      <header className="border-b border-[#2A2F38]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <a href="/" className="flex items-center gap-2 text-[#9CA3AF] hover:text-[#E5E7EB] transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Voltar</span>
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-16 px-6">
        <div className="max-w-lg mx-auto">
          {/* Title */}
          <div className="text-center mb-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              <span className="text-[#F59E0B]">Kp</span>
              <span className="text-[#E5E7EB]">Action</span>
            </h1>
            <h2 className="font-heading text-xl text-[#E5E7EB] mb-2">
              Iniciar Teste Gratuito
            </h2>
            <p className="text-[#9CA3AF]">
              30 dias de acesso completo, sem compromisso
            </p>
          </div>

          {/* Form */}
          <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-[#9CA3AF] uppercase tracking-wider flex items-center gap-2">
                  <User className="h-4 w-4 text-[#F59E0B]" />
                  Nome Completo *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Seu nome completo"
                  className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] placeholder:text-[#6B7280] focus:border-[#F59E0B]"
                  data-testid="trial-form-name"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-[#9CA3AF] uppercase tracking-wider flex items-center gap-2">
                  <Mail className="h-4 w-4 text-[#F59E0B]" />
                  Email Corporativo *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="seu@empresa.com"
                  className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] placeholder:text-[#6B7280] focus:border-[#F59E0B]"
                  data-testid="trial-form-email"
                />
              </div>

              {/* Razão Social */}
              <div className="space-y-2">
                <Label htmlFor="company" className="text-sm font-medium text-[#9CA3AF] uppercase tracking-wider flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-[#F59E0B]" />
                  Razão Social *
                </Label>
                <Input
                  id="company"
                  name="company"
                  type="text"
                  required
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Razão social da empresa"
                  className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] placeholder:text-[#6B7280] focus:border-[#F59E0B]"
                  data-testid="trial-form-company"
                />
              </div>

              {/* CNPJ */}
              <div className="space-y-2">
                <Label htmlFor="cnpj" className="text-sm font-medium text-[#9CA3AF] uppercase tracking-wider">
                  CNPJ *
                </Label>
                <Input
                  id="cnpj"
                  name="cnpj"
                  type="text"
                  required
                  value={formData.cnpj}
                  onChange={handleChange}
                  placeholder="00.000.000/0000-00"
                  className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] placeholder:text-[#6B7280] focus:border-[#F59E0B]"
                  data-testid="trial-form-cnpj"
                />
              </div>

              {/* Cargo */}
              <div className="space-y-2">
                <Label htmlFor="position" className="text-sm font-medium text-[#9CA3AF] uppercase tracking-wider flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-[#F59E0B]" />
                  Cargo *
                </Label>
                <Input
                  id="position"
                  name="position"
                  type="text"
                  required
                  value={formData.position}
                  onChange={handleChange}
                  placeholder="Seu cargo na empresa"
                  className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] placeholder:text-[#6B7280] focus:border-[#F59E0B]"
                  data-testid="trial-form-position"
                />
              </div>

              {/* Employees */}
              <div className="space-y-2">
                <Label htmlFor="employees" className="text-sm font-medium text-[#9CA3AF] uppercase tracking-wider">
                  Número de Funcionários *
                </Label>
                <Select 
                  value={formData.employees} 
                  onValueChange={(value) => handleSelectChange("employees", value)}
                  required
                >
                  <SelectTrigger 
                    className="bg-[#0F1115] border-[#2A2F38] text-[#E5E7EB] focus:border-[#F59E0B]"
                    data-testid="trial-form-employees"
                  >
                    <SelectValue placeholder="Selecione *" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1F26] border-[#2A2F38]">
                    <SelectItem value="1-10" className="text-[#E5E7EB] focus:bg-[#2A2F38]">1-10</SelectItem>
                    <SelectItem value="11-25" className="text-[#E5E7EB] focus:bg-[#2A2F38]">11-25</SelectItem>
                    <SelectItem value="26-50" className="text-[#E5E7EB] focus:bg-[#2A2F38]">26-50</SelectItem>
                    <SelectItem value="50+" className="text-[#E5E7EB] focus:bg-[#2A2F38]">Mais de 50</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#10B981] text-white font-bold uppercase tracking-wide hover:bg-[#059669] transition-all rounded-sm py-4 mt-6"
                data-testid="trial-form-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Processando...
                  </>
                ) : (
                  "Iniciar Meu Teste Gratuito"
                )}
              </Button>
            </form>

            {/* Privacy */}
            <p className="text-xs text-[#6B7280] text-center mt-6">
              Ao se cadastrar, você concorda com nossa{" "}
              <a href="#privacy" className="text-[#F59E0B] hover:underline">
                Política de Privacidade
              </a>
              {" "}e{" "}
              <a href="/termos-de-licenca" className="text-[#F59E0B] hover:underline">
                Termos de Licença
              </a>
              .
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
